import { ModalViewModel } from '@/infra/ModalViewModel'
import { task } from 'mobx-task'
import { action, computed, observable } from 'mobx'
import { type Id } from '@/infra/id'
import { Workspace } from '@/features/iam/workspaces/Workspace'
import type { AlertErrorHandlerFactory, AlertFactory } from '@/features/shell'
import { type SearchableUser } from '@/features/iam/users/SearchableUser'
import { func, required, validationContext } from 'validx'
import type { UpdateMemberFn } from '@/features/iam'

export class RemoveFromWorkspaceDialogViewModel {
  readonly modal = new ModalViewModel()

  @observable
  private accessor _selectedUserIds = new Set<Id>()

  @observable
  accessor selectedUser: SearchableUser | undefined = undefined

  @observable
  accessor workspace: Workspace = Workspace.none

  @observable
  accessor confirmation = ''

  validation = validationContext(this, {
    confirmation: [
      required({ msg: 'This field is required' }),
      func(
        ({ value }: { value?: string }) =>
          value?.toLowerCase() === 'remove' || 'You must type "remove"',
      ),
    ],
  })

  constructor(
    private readonly _onRemoved: (userPublicIds: Id[]) => void,
    private readonly _updateMember: UpdateMemberFn,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
  ) {}

  @computed
  get selectedUserIds() {
    return Array.from(this._selectedUserIds)
  }

  @computed
  get selectedUserName() {
    return this.selectedUser ? this.selectedUser.displayName : null
  }

  @action.bound
  open(selectedUserIds: Set<Id>, workspace: Workspace) {
    this._selectedUserIds = selectedUserIds
    this.workspace = workspace
    this.modal.show()
  }

  @action.bound
  showWithUser(user: SearchableUser, workspace: Workspace) {
    this.selectedUser = user
    this._selectedUserIds = new Set([user.publicId])
    this.workspace = workspace
    this.modal.show()
  }

  submit = task.resolved(async () => {
    if (!this.validation.reset().validate().isValid) {
      return
    }

    const alert = this._newAlert()
      .withState('pending')
      .saying('Removing User!')
      .autoDismiss()
      .show()

    try {
      await Promise.all(
        this.selectedUserIds.map((userPublicId) =>
          this._updateMember({
            workspaceId: this.workspace.id,
            userPublicId,
            inactive: true,
          }),
        ),
      )
    } catch (err) {
      alert.dismiss()
      this._createAlertErrorHandler('Removing users failed')(err)
    }

    this._onRemoved(this.selectedUserIds)

    alert
      .withState('success')
      .saying('Success!', 'User removed successfully!')
      .autoDismiss()
      .show()

    this.close()
  })

  @action.bound
  close() {
    this.selectedUser = undefined
    this.workspace = Workspace.none
    this.validation.reset()
    this.confirmation = ''
    this.modal.close()
  }
}
